import { useState } from 'react';
import { Dialog } from '@mui/material';
import DifferenceOutlinedIcon from '@mui/icons-material/DifferenceOutlined';

import type { IncidentDataSnapshot } from '@models/incidents';

import s from './IncidentDataChangeDialog.module.css';

type IncidentDataChangeDialogProps = {
  /** Who the snapshot belongs to, shown in the dialog subtitle (customer email, incident name…). */
  subject: string;
  previousData: IncidentDataSnapshot | null;
  newData: IncidentDataSnapshot | null;
};

function formatSnapshot(value: IncidentDataSnapshot | null): string {
  return value ? JSON.stringify(value, null, 2) : '—';
}

type DiffNode =
  | { kind: 'leaf'; from: unknown; to: unknown }
  | { kind: 'branch'; isArray: boolean; children: DiffChild[] };

type DiffChild = { key: string; node: DiffNode } | { gap: true };

type DiffTone = 'context' | 'removed' | 'added' | 'gap';

type DiffLine = { depth: number; tone: DiffTone; text: string };

const DIFF_SIGNS: Record<DiffTone, string> = { context: ' ', removed: '-', added: '+', gap: ' ' };

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

// Walks both snapshots in step and keeps only the branches that lead to a changed value, so the
// summary shows each edited field in place inside the JSON structure instead of flagging the whole
// top-level key; a run of unchanged siblings collapses into one gap. Missing and null are equal.
function diffValues(from: unknown, to: unknown): DiffNode | null {
  const isArray = Array.isArray(from) && Array.isArray(to);
  if (!isArray && !(isPlainObject(from) && isPlainObject(to))) {
    return JSON.stringify(from ?? null) === JSON.stringify(to ?? null)
      ? null
      : { kind: 'leaf', from, to };
  }
  const fromRecord = from as Record<string, unknown>;
  const toRecord = to as Record<string, unknown>;
  const keys = new Set([...Object.keys(fromRecord), ...Object.keys(toRecord)]);
  const children: DiffChild[] = [];
  for (const key of keys) {
    const node = diffValues(fromRecord[key], toRecord[key]);
    if (node) children.push({ key, node });
    else if (!children.length || !('gap' in children[children.length - 1])) {
      children.push({ gap: true });
    }
  }
  return children.some((child) => 'node' in child) ? { kind: 'branch', isArray, children } : null;
}

// Prints the diff tree as JSON lines: a changed value becomes a removed line with the old value
// and an added line with the new one; a side that was missing prints no line.
function diffLines(node: DiffNode, label: string, depth: number, comma: string, lines: DiffLine[]) {
  if (node.kind === 'leaf') {
    if (node.from !== undefined) {
      lines.push({ depth, tone: 'removed', text: `${label}${JSON.stringify(node.from)}${comma}` });
    }
    if (node.to !== undefined) {
      lines.push({ depth, tone: 'added', text: `${label}${JSON.stringify(node.to)}${comma}` });
    }
    return lines;
  }
  lines.push({ depth, tone: 'context', text: `${label}${node.isArray ? '[' : '{'}` });
  node.children.forEach((child, i) => {
    if ('gap' in child) {
      lines.push({ depth: depth + 1, tone: 'gap', text: '…' });
      return;
    }
    const childLabel = node.isArray ? '' : `${JSON.stringify(child.key)}: `;
    const childComma = i < node.children.length - 1 ? ',' : '';
    diffLines(child.node, childLabel, depth + 1, childComma, lines);
  });
  lines.push({ depth, tone: 'context', text: `${node.isArray ? ']' : '}'}${comma}` });
  return lines;
}

/** Renders nothing when the attachment carries no snapshot at all. */
function IncidentDataChangeDialog({
  subject,
  previousData,
  newData,
}: IncidentDataChangeDialogProps) {
  const [open, setOpen] = useState(false);
  if (!previousData && !newData) return null;

  const diff = diffValues(previousData ?? {}, newData ?? {});
  const lines = diff ? diffLines(diff, '', 0, '', []) : [];

  return (
    <>
      <button
        type="button"
        className={s.trigger}
        onClick={() => setOpen(true)}
        title="Show data before and after the change"
        aria-label={`Show data change for ${subject}`}>
        <DifferenceOutlinedIcon sx={{ fontSize: 14 }} aria-hidden />
        Before / after
      </button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '14px',
              width: 760,
              maxWidth: '94vw',
              boxShadow: '0 24px 64px rgba(15, 23, 42, 0.18), 0 4px 12px rgba(15, 23, 42, 0.08)',
            },
          },
        }}>
        <div className={s.body}>
          <h3 className={s.title}>Data change</h3>
          <p className={s.subtitle}>{subject}</p>

          {lines.length > 0 && (
            <section className={s.changes}>
              <h4 className={s.columnTitle}>Changes</h4>
              <pre className={s.diff}>
                {lines.map((line, i) => (
                  <span key={i} className={`${s.diffLine} ${s[line.tone]}`}>
                    <span className={s.diffSign} aria-hidden>
                      {DIFF_SIGNS[line.tone]}
                    </span>
                    <span style={{ paddingLeft: `${line.depth * 2}ch` }}>{line.text}</span>
                  </span>
                ))}
              </pre>
            </section>
          )}

          <div className={s.columns}>
            <section className={s.column}>
              <h4 className={s.columnTitle}>Before</h4>
              <pre className={s.json}>{formatSnapshot(previousData)}</pre>
            </section>
            <section className={s.column}>
              <h4 className={s.columnTitle}>After</h4>
              <pre className={s.json}>{formatSnapshot(newData)}</pre>
            </section>
          </div>

          <div className={s.actions}>
            <button type="button" className={s.closeBtn} onClick={() => setOpen(false)}>
              Close
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default IncidentDataChangeDialog;
